// frontend/src/components/book/atelier/framing.js
//
// CADRAGE D'UNE PHOTO DANS SON EMPLACEMENT (refonte de l'atelier, phase 4a) —
// logique pure, sans React ni DOM, donc testable seule. Miroir de
// backend/services/composition/framing.js (le serveur est la reference ; ceci
// ne fait que parler son format et calculer ce que l'ecran doit montrer).
//
// Un cadrage : { fit: 'cover' | 'contain', focusX: 0..100, focusY: 0..100,
// zoom: 1..2.5 }, par EMPLACEMENT (identifiant stable, voir newSlotIds).
// Valeurs par defaut : cover, 50, 50, 1 (une photo REMPLIT son cadre).

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 2.5;

export const FRAMING_DEFAULT = Object.freeze({ fit: 'cover', focusX: 50, focusY: 50, zoom: 1 });

// Au-dela de cette part d'image coupee par le remplissage, un petit point
// orange previent l'utilisateur (jamais une fenetre).
export const CROP_WARNING_THRESHOLD = 0.3;

const randomPart = () => Math.random().toString(36).slice(2, 8).padEnd(6, '0');

/** Un identifiant d'emplacement stable et opaque (jamais l'index). */
export const newSlotId = () => `s_${randomPart()}`;
export const newSlotIds = (count) => Array.from({ length: count }, newSlotId);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
export const clampZoom = (zoom) => clamp(Number.isFinite(zoom) ? zoom : 1, ZOOM_MIN, ZOOM_MAX);

/** Cadrage complet (valeurs par defaut pour tout ce qui manque). */
export function normalizeFraming(framing) {
  return {
    fit: framing && framing.fit === 'contain' ? 'contain' : 'cover',
    focusX: clamp(Number.isFinite(framing?.focusX) ? framing.focusX : 50, 0, 100),
    focusY: clamp(Number.isFinite(framing?.focusY) ? framing.focusY : 50, 0, 100),
    zoom: clampZoom(framing?.zoom)
  };
}

export const isDefaultFraming = (framing) => {
  const f = normalizeFraming(framing);
  return f.fit === 'cover' && f.focusX === 50 && f.focusY === 50 && f.zoom === 1;
};

// --- ancien format : { focalX, focalY (0..1), zoom, fitMode } -------------

/** Lecture de l'ancien format (par photo) vers un cadrage d'emplacement. */
export function framingFromAdjustment(adjustment) {
  if (!adjustment || typeof adjustment !== 'object') return { ...FRAMING_DEFAULT };
  return normalizeFraming({
    fit: adjustment.fitMode === 'contain' ? 'contain' : 'cover',
    focusX: Number.isFinite(adjustment.focalX) ? adjustment.focalX * 100 : 50,
    focusY: Number.isFinite(adjustment.focalY) ? adjustment.focalY * 100 : 50,
    zoom: adjustment.zoom
  });
}

/** Cadrage d'emplacement vers l'ancien format (couverture : cover_overrides). */
export function adjustmentFromFraming(framing) {
  const f = normalizeFraming(framing);
  return { focalX: f.focusX / 100, focalY: f.focusY / 100, zoom: f.zoom, fitMode: f.fit };
}

// --- geometrie ---------------------------------------------------------------

// Taille a laquelle object-fit:cover affiche l'image dans son cadre
// (avant zoom) : la plus petite echelle qui couvre tout le cadre.
function coverSize(frameW, frameH, imageRatio) {
  const frameRatio = frameW / frameH;
  return imageRatio > frameRatio
    ? { w: frameH * imageRatio, h: frameH }
    : { w: frameW, h: frameW / imageRatio };
}

// Taille a laquelle object-fit:contain affiche l'image (toute l'image visible).
function containSize(frameW, frameH, imageRatio) {
  const frameRatio = frameW / frameH;
  return imageRatio > frameRatio
    ? { w: frameW, h: frameW / imageRatio }
    : { w: frameH * imageRatio, h: frameH };
}

/**
 * Part de l'image COUPEE par le remplissage (0 = rien, 0,4 = 40 %).
 * `imageRatio`/`frameRatio` : largeur / hauteur. A zoom 1, une photo plus
 * large que son cadre perd (1 - frameRatio/imageRatio) de sa surface ; le zoom
 * coupe encore (1 - 1/zoom^2 de plus). Mode "entiere" : rien n'est coupe.
 */
export function croppedFraction({ imageRatio, frameRatio, zoom = 1, fit = 'cover' }) {
  if (fit === 'contain') return 0;
  if (!(imageRatio > 0) || !(frameRatio > 0)) return 0;
  const shown = Math.min(imageRatio / frameRatio, frameRatio / imageRatio); // part visible a zoom 1
  const visible = shown / (clampZoom(zoom) ** 2);
  return clamp(1 - visible, 0, 1);
}

/**
 * Nouveau point de focus quand l'utilisateur FAIT GLISSER la photo de
 * (dxPx, dyPx) pixels dans son cadre : le contenu suit le pointeur.
 *
 * Maths : l'image est dessinee avec object-position fx/fy puis zoomee autour de
 * ce MEME point (transform-origin = fx/fy) ; le bord gauche de l'image se
 * retrouve a x' = fx * (frameW - zoom * imageW) — LINEAIRE en fx. Deplacer le
 * contenu de dx pixels demande donc dfx = dx / (frameW - zoom * imageW).
 * Meme chose en y. Sans debordement sur un axe (denominateur nul), cet axe ne
 * bouge pas. Resultat en pourcentage 0..100.
 */
export function panFocus({ focusX, focusY, dxPx, dyPx, frameW, frameH, imageRatio, zoom = 1, fit = 'cover' }) {
  if (!(frameW > 0) || !(frameH > 0) || !(imageRatio > 0)) return { focusX, focusY };
  // Mode "Entiere" : la photo est plus petite que son cadre, on la DEPLACE dans le
  // cadre (ex. une photo portrait sur une double page : la poser sur une page ou
  // sur l'autre). Meme formule, avec la taille "contain".
  const { w, h } = fit === 'contain' ? containSize(frameW, frameH, imageRatio) : coverSize(frameW, frameH, imageRatio);
  const z = clampZoom(zoom);
  const denomX = frameW - z * w;
  const denomY = frameH - z * h;
  const nextX = Math.abs(denomX) < 0.5 ? focusX : focusX + (dxPx / denomX) * 100;
  const nextY = Math.abs(denomY) < 0.5 ? focusY : focusY + (dyPx / denomY) * 100;
  return { focusX: clamp(nextX, 0, 100), focusY: clamp(nextY, 0, 100) };
}

/**
 * Glissement d'une photo sur DOUBLE PAGE (decaler la photo entiere pour eloigner
 * un visage du pli). Une photo qui remplit deja le cadre dans un sens n'a, dans
 * ce sens, aucune marge de manoeuvre : la deplacer ferait apparaitre du blanc.
 * Ici le contenu situe SOUS LE POINTEUR suit le pointeur, et quand cela depasse
 * la marge le zoom augmente JUSTE ce qu'il faut pour que la photo continue a
 * couvrir tout le cadre (jamais de blanc) ; en revenant vers le depart, le zoom
 * redescend. Un petit tremblement (zone morte) sur un axe sans marge ne zoome pas.
 *
 * Maths, par axe (F = taille du cadre, S = taille de l'image a zoom 1, z = zoom,
 * a = position du pointeur dans le cadre, d = deplacement) : le bord de l'image
 * est a p = f * (F - z*S) ; le point sous le pointeur est a la fraction
 * u = (a - p) / (z*S) de l'image. Apres glissement il doit etre en a + d, donc
 * p' = a + d - z'*S*u, et l'image couvre le cadre si F - z'*S <= p' <= 0, soit
 * z' >= (a + d) / (S*u) et z' >= (F - a - d) / (S*(1-u)). On prend le plus petit
 * z' (au moins le zoom de depart), puis f' = p' / (F - z'*S).
 */
export function panFocusAutoZoom({
  focusX, focusY, dxPx, dyPx, frameW, frameH, imageRatio, zoom = 1,
  anchorX = frameW / 2, anchorY = frameH / 2, deadZoneRatio = 0.015
}) {
  if (!(frameW > 0) || !(frameH > 0) || !(imageRatio > 0)) return { focusX, focusY, zoom: clampZoom(zoom) };
  const { w, h } = coverSize(frameW, frameH, imageRatio);
  const z0 = clampZoom(zoom);
  const axis = (focus, delta, frame, size, anchor) => {
    const slack = frame - z0 * size;
    const p = (focus / 100) * slack;
    const dead = frame * deadZoneRatio;
    const move = Math.abs(slack) < 0.5 && Math.abs(delta) < dead ? 0 : delta;
    const a = clamp(anchor, 0, frame);
    const u = clamp((a - p) / (z0 * size), 0.0001, 0.9999);
    const needed = Math.max((a + move) / (size * u), (frame - a - move) / (size * (1 - u)));
    return { a, u, move, needed };
  };
  const ax = axis(focusX, dxPx, frameW, w, anchorX);
  const ay = axis(focusY, dyPx, frameH, h, anchorY);
  const z = clampZoom(Math.max(z0, ax.needed, ay.needed));
  const resolve = (info, frame, size, fallback) => {
    const slack = frame - z * size;
    if (Math.abs(slack) < 0.5) return fallback;
    const p = clamp(info.a + info.move - z * size * info.u, Math.min(slack, 0), 0);
    return clamp((p / slack) * 100, 0, 100);
  };
  return { focusX: resolve(ax, frameW, w, focusX), focusY: resolve(ay, frameH, h, focusY), zoom: z };
}

/** Zoom apres un cran de molette ou un pincement (facteur multiplicatif). */
export const zoomBy = (zoom, factor) => clampZoom(zoom * factor);
